import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchNewsletters,
  createNewsletter,
  updateNewsletter,
  deleteNewsletter,
} from "../../api/controller/admin/adminNewsLetters";
import AdminNewsletterDialog from "./adminDialogBoxes/AdminNewsletterDialog";
import AdminConfirmDialog from "./adminDialogBoxes/AdminConfirmDialog";
import { formatDisplayDate } from "../helper/date";
import { useFlashMessage } from "../../components/FlashMessageProvider";
const AdminNewsletters = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showMessage } = useFlashMessage();

  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchNewsletters();
        if (mounted) setItems(res || []);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load newsletters.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have permission to view this page.</p>
        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  const handleDialogSave = async (values) => {
    setIsSaving(true);
    try {
      const isEditing = Boolean(editing);
      if (editing) {
        await updateNewsletter(editing, values);
      } else {
        await createNewsletter(values);
      }
      const res = await fetchNewsletters();
      setItems(res || []);
      showMessage(isEditing ? "Newsletter updated successfully." : "Newsletter created successfully.", { type: "success" });
      setDialogOpen(false);
      setDialogInitial(null);
      setEditing(null);
    } catch (err) {
      console.error(err);
      showMessage("Failed to save newsletter. Check console for details.", { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = () => {
    setDialogInitial(null);
    setEditing(null);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setDialogInitial(item);
    setEditing(item._id || item.id);
    setDialogOpen(true);
  };

  const handleDeleteRequest = (item) => {
    setDeleteTarget({ id: item._id || item.id, title: item.title });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteNewsletter(deleteTarget.id);
      const res = await fetchNewsletters();
      setItems(res || []);
      showMessage("Newsletter deleted.", { type: "success" });
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      showMessage("Failed to delete newsletter.", { type: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Admin — Manage Newsletters</h1>

      <div className="mb-4">
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded">Add Newsletter</button>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading newsletters...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item._id || item.id} className="bg-white p-4 rounded shadow flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.type} • {formatDisplayDate(item.date)}</p>
                <p className="mt-2 text-gray-700">{item.description}</p>
                <p className="mt-2 text-gray-700">Active: {item.active ? "Yes" : "No"}</p>
                <div className="mt-3">
                  <button onClick={() => handleEdit(item)} className="text-blue-600 mr-4">Edit</button>
                  <button onClick={() => handleDeleteRequest(item)} className="text-red-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <AdminNewsletterDialog
        open={dialogOpen}
        initial={dialogInitial}
        onClose={() => {
          if (isSaving) return;
          setDialogOpen(false);
          setDialogInitial(null);
          setEditing(null);
        }}
        onSave={handleDialogSave}
        isSaving={isSaving}
      />
      <AdminConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete Newsletter"
        message={deleteTarget?.title ? `Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.` : "Are you sure you want to delete this newsletter?"}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default AdminNewsletters;
